import React, { useMemo, useState, useRef } from 'react';
import { cloneDeep } from 'lodash';

import { CellValueChangedEvent, GridApi } from 'ag-grid-community';

import { TableGrid } from '@/components/common/TableGrid/TableGrid';
import AgGridDropdownEditor
  from '@/components/common/TableGrid/CustomComponents/AgGridDropdown/AgGridDropdownEditor';
import AgGridDatepickerTag4Editor
  from '@/components/common/TableGrid/CustomComponents/AgGridDatepicker/AgGridDatepickerTag4Editor';
import AgGridCellRenderer
  from '@/components/common/TableGrid/CustomComponents/AgGridCellRenderer/AgGridCellRenderer';
import { useColumns } from '../grid/columns';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import useUpdateBulkDnr, { UpdateBulkDnrDto } from '../hooks/useUpdateBulkDnr';
import { useVariableColumns } from '../grid/variableColumns';
import { VariableContractLineOutput } from '@/graphql/contracts/_generated_/contracts';
import { validator } from '../grid/validation/validator';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import { isDnrDateOutOfRenewalRange } from '../components/utils';

import styles from './dnr-bulk-update-modal.module.scss';

interface IDnrLinesTableProps {
  dnrContractLines: ContractLine[];
  contract: Contract;
  onSaveComplete?: () => void;
}

export default function DnrLinesTable({
  dnrContractLines,
  contract,
  onSaveComplete
}: IDnrLinesTableProps) {
  const [editedFields, setEditedFields] = useState<Map<string, Set<string>>>(new Map());
  const [isConfirmWindowVisible, setIsConfirmWindowVisible] = useState<boolean>(false);
  const [isDnrWarningWindowVisible, setIsDnrWarningWindowVisible] = useState<boolean>(false);
  const { updateBulkDnr } = useUpdateBulkDnr();
  const gridApiRef = useRef<GridApi | null>(null);

  const mutableContractLineSource = useMemo(() => {
    return cloneDeep(dnrContractLines);
  }, [dnrContractLines]);

  const getValidationErrors = (colDef: any, data: any, context: any): string[] => {
    const contractLineId = data.contractLineID || data.id || data.iD;
    const fieldName = colDef?.field;
    const editedFieldsForRow = editedFields.get(contractLineId);

    if (fieldName === 'noFurtherBillingDate') {
      const hasFieldBeenEdited = editedFieldsForRow?.has(fieldName);
      return hasFieldBeenEdited ? validator.getErrorsAt(fieldName, data, context) : [];
    }

    const hasRowBeenEdited = editedFieldsForRow && editedFieldsForRow.size > 0;
    return hasRowBeenEdited ? validator.getErrorsAt(fieldName, data, context) : [];
  };

  const ordinaryColumns = useColumns(dnrContractLines, getValidationErrors);
  const variableColumns = useVariableColumns(
    dnrContractLines as unknown as VariableContractLineOutput[],
    getValidationErrors
  );
  const columns = dnrContractLines[0]?.billingType?.formattedType ? ordinaryColumns : variableColumns;

  const onCellValueChanged = (event: CellValueChangedEvent) => {
    const updatedLine = event.data;
    const contractLineId = 'iD' in updatedLine ? updatedLine.iD
      : 'id' in updatedLine ? updatedLine.id : updatedLine.contractLineID;
    const changedField = event.colDef?.field;

    setEditedFields(prev => {
      const newMap = new Map(prev);
      const existingFields = newMap.get(contractLineId) || new Set<string>();
      if (changedField) {
        existingFields.add(changedField);
      }
      newMap.set(contractLineId, existingFields);
      return newMap;
    });

    event.api.redrawRows({ rowNodes: [event.node] });
  };

  const hasAnyTableFieldBeenEdited = editedFields.size > 0;
  const hasValidationErrors = useMemo(() => {
    const editedLineIds = Array.from(editedFields.keys());

    return editedLineIds.some((lineId) => {
      const line = mutableContractLineSource.find((cl) => {
        const id = 'iD' in cl ? cl.iD : 'id' in cl ? cl.id : cl.contractLineID;
        return id === lineId;
      });

      if (!line) return false;

      const editedFieldsForRow = editedFields.get(lineId);
      const fieldsToValidate = ['statusChangeDate', 'statusChangeComment'];

      if (editedFieldsForRow?.has('noFurtherBillingDate')) {
        fieldsToValidate.push('noFurtherBillingDate');
      }

      return fieldsToValidate.some(fieldName => {
        return !validator.isValidAt(fieldName, line, { contract });
      });
    });
  }, [editedFields, mutableContractLineSource, contract]);

  const onSubmitTableSave = () => {
    const editedLineIds = Array.from(editedFields.keys());
    const hasAnyDnrDateOutOfRange = editedLineIds.some((lineId) => {
      const editedFieldsForRow = editedFields.get(lineId);
      if (editedFieldsForRow?.has('statusChangeDate')) {
        const line = mutableContractLineSource.find((cl) => {
          const id = 'iD' in cl ? cl.iD : 'id' in cl ? cl.id : cl.contractLineID;
          return id === lineId;
        });
        if (line?.statusChangeDate && contract.contractEndDate) {
          return isDnrDateOutOfRenewalRange(
            new Date(line.statusChangeDate),
            new Date(contract.contractEndDate)
          );
        }
      }
      return false;
    });

    if (hasAnyDnrDateOutOfRange) {
      setIsDnrWarningWindowVisible(true);
      return;
    }

    setIsConfirmWindowVisible(true);
  };

  const onCloseDnrWarningWindow = () => {
    setIsDnrWarningWindowVisible(false);
  };

  const onCloseConfirmWindow = () => {
    setIsConfirmWindowVisible(false);
  };

  const onConfirmDnrWarning = () => {
    setIsDnrWarningWindowVisible(false);
    setIsConfirmWindowVisible(true);
  };

  const onConfirm = () => {
    const editedLineIds = Array.from(editedFields.keys());

    if (editedLineIds.length === 0) {
      setIsConfirmWindowVisible(false);
      return;
    }

    const updatedData: UpdateBulkDnrDto = {
      contractId: contract.contractID,
      dnrContractLineData: mutableContractLineSource
        .filter((cl) => {
          const contractLineId = 'iD' in cl ? cl.iD : 'id' in cl ? cl.id : cl.contractLineID;
          return editedLineIds.includes(contractLineId);
        })
        .map((cl) => {
          const contractLineID = 'iD' in cl ? cl.iD : 'id' in cl ? cl.id : cl.contractLineID;

          return {
            contractLineID,
            dnrDate: cl.statusChangeDate,
            reasonCode: (cl.statusChangeReason?.code ?? cl.statusChangeReason) as string,
            comment: cl.statusChangeComment as string,
            noFurtherBilling: cl.noFurtherBilling ?? false,
            noFurtherBillingDate: cl.noFurtherBillingDate,
          };
        }),
    };

    let hasErrors = false;
    updatedData.dnrContractLineData.forEach((lineData) => {
      const line = mutableContractLineSource.find((cl) => {
        const id = 'iD' in cl ? cl.iD : 'id' in cl ? cl.id : cl.contractLineID;
        return id === lineData.contractLineID;
      });

      if (line) {
        const editedFieldsForRow = editedFields.get(lineData.contractLineID);
        const fieldsToValidate = ['statusChangeDate', 'statusChangeComment'];
        if (editedFieldsForRow?.has('noFurtherBillingDate')) {
          fieldsToValidate.push('noFurtherBillingDate');
        }

        fieldsToValidate.forEach(fieldName => {
          if (!validator.isValidAt(fieldName, line, { contract })) {
            hasErrors = true;
          }
        });
      }
    });

    if (hasErrors) {
      setIsConfirmWindowVisible(false);
      return;
    }

    updateBulkDnr(updatedData);
    setEditedFields(new Map());
    onSaveComplete?.();
    setIsConfirmWindowVisible(false);
  };

  const onGridReady = (params: { api: GridApi }) => {
    gridApiRef.current = params.api;
  };

  return (
        <>
            {isDnrWarningWindowVisible && (
                <AgConfirmationModal
                    isOpen={true}
                    title="You are about to set DNR date that is out of Contract Renewal window"
                    onClose={onCloseDnrWarningWindow}
                    onConfirm={onConfirmDnrWarning}
                    contentStyle={{ width: 680 }}
                >
                    The DNR Date set for the Contract/Contract Lines are out of range of Contract Renewal.
                    <br />
                    Please check the DNR Date and confirm the selection.
                </AgConfirmationModal>
            )}

            {isConfirmWindowVisible && (
                <AgConfirmationModal
                    isOpen={true}
                    title="You are about to update DNR Contract Line(s)"
                    onClose={onCloseConfirmWindow}
                    onConfirm={onConfirm}
                    contentStyle={{ width: 600 }}
                >
                    Updating information for DNR Contract Line(s) could impact billing.<br />
                    Use it only with extra precaution.
                </AgConfirmationModal>
            )}

            <div className={styles['section-label']}>Update details about individual lines</div>

            <div className={styles['grid-container']}>
                <TableGrid
                    rowData={mutableContractLineSource}
                    columnDefs={columns}
                    pagination={false}
                    suppressPaginationPanel={true}
                    components={{
                      DropdownEditor: AgGridDropdownEditor,
                      DatepickerEditor: AgGridDatepickerTag4Editor,
                      CellRenderer: AgGridCellRenderer,
                    }}
                    context={{ contract }}
                    onCellValueChanged={onCellValueChanged}
                    onGridReady={onGridReady}
                    getRowId={(el) =>
                      (el.data as any)?.contractLineID ??
                        (el.data as any)?.iD ??
                        (el.data as any)?.id
                    }
                    defaultColDef={{
                      suppressMovable: true,
                      sortable: false,
                      resizable: false
                    }}
                />
            </div>

            <div className={styles['button-after-table']}>
                <Button
                    className={styles['button-save']}
                    disabled={!hasAnyTableFieldBeenEdited || hasValidationErrors}
                    buttonStyleType={ButtonStyleType.PRIMARY}
                    onClick={onSubmitTableSave}
                >
                    Save
                </Button>
            </div>
        </>
  );
}
